import { useMemo } from "react";
import dayjs from "dayjs";
import adminUsers from "../../../constants/adminUsers";

const useStatsCalculations = (analytics, users) => {
  return useMemo(() => {
    const {
      totalUsers,
      totalVisits,
      tasksCompleted,
      tasksCreated,
      tasksDeleted,
    } = analytics;

    // Check if we have pre-computed stats from the backend
    // This is used when users array is not loaded yet (stats-only endpoint)
    const hasPreComputedStats = analytics.nonAdminUserCount !== undefined;
    const hasUsers = users && users.length > 0;

    // Filter out admin users from all calculations (only when users are loaded)
    const nonAdminUsers = hasUsers
      ? users.filter((user) => !adminUsers.includes(user.userId))
      : [];

    // Use pre-computed stats from backend if available, otherwise calculate from users
    const nonAdminUserCount = hasPreComputedStats
      ? analytics.nonAdminUserCount
      : nonAdminUsers.length;

    // Calculate insights (excluding admin users)
    const nonAdminTasksCreated = hasPreComputedStats
      ? analytics.nonAdminTasksCreated
      : nonAdminUsers.reduce((sum, user) => sum + (user.tasksCreated || 0), 0);
    const nonAdminTasksCompleted = hasPreComputedStats
      ? analytics.nonAdminTasksCompleted
      : nonAdminUsers.reduce(
          (sum, user) => sum + (user.tasksCompleted || 0),
          0,
        );
    const nonAdminTotalVisits = hasPreComputedStats
      ? analytics.nonAdminTotalVisits
      : nonAdminUsers.reduce((sum, user) => sum + (user.totalVisits || 0), 0);

    const completionRate = hasPreComputedStats
      ? analytics.completionRate
      : nonAdminTasksCreated > 0
        ? Math.round((nonAdminTasksCompleted / nonAdminTasksCreated) * 100)
        : 0;
    const deletionRate =
      tasksCreated > 0 ? Math.round((tasksDeleted / tasksCreated) * 100) : 0;
    const avgVisitsPerUser = hasPreComputedStats
      ? analytics.avgVisitsPerUser
      : nonAdminUserCount > 0
        ? Math.round(nonAdminTotalVisits / nonAdminUserCount)
        : 0;
    const avgTasksPerUser = hasPreComputedStats
      ? analytics.avgTasksPerUser
      : nonAdminUserCount > 0
        ? Math.round(nonAdminTasksCreated / nonAdminUserCount)
        : 0;

    // Get recent activity insights (excluding admin users)
    // Use pre-computed stats when available
    const activeUsers = hasPreComputedStats
      ? analytics.activeUsersThisWeek
      : nonAdminUsers.filter(
          (user) => dayjs().diff(dayjs(user.lastVisited), "day") <= 7,
        ).length;
    const newUsersThisWeek = hasPreComputedStats
      ? analytics.newUsersThisWeek
      : nonAdminUsers.filter(
          (user) => dayjs().diff(dayjs(user.createdAt), "day") <= 7,
        ).length;
    const newUsersToday = hasPreComputedStats
      ? analytics.newUsersToday
      : nonAdminUsers.filter(
          (user) => dayjs().diff(dayjs(user.createdAt), "day") === 0,
        ).length;
    const newUsersThisMonth = hasPreComputedStats
      ? analytics.newUsersThisMonth
      : nonAdminUsers.filter(
          (user) => dayjs().diff(dayjs(user.createdAt), "month") === 0,
        ).length;

    // Tasks created by time period
    const tasksCreatedThisWeek = hasPreComputedStats
      ? analytics.tasksCreatedThisWeek
      : nonAdminUsers
          .filter((user) => dayjs().diff(dayjs(user.createdAt), "day") <= 7)
          .reduce((sum, user) => sum + (user.tasksCreated || 0), 0);

    const tasksCreatedToday = hasPreComputedStats
      ? analytics.tasksCreatedToday
      : nonAdminUsers
          .filter((user) => dayjs().diff(dayjs(user.createdAt), "day") === 0)
          .reduce((sum, user) => sum + (user.tasksCreated || 0), 0);

    const tasksCreatedThisMonth = hasPreComputedStats
      ? analytics.tasksCreatedThisMonth
      : nonAdminUsers
          .filter((user) => dayjs().diff(dayjs(user.createdAt), "month") === 0)
          .reduce((sum, user) => sum + (user.tasksCreated || 0), 0);

    // Visit calculations - use pre-computed stats when available
    let visitsToday, visitsThisWeek, visitsThisMonth;

    if (hasPreComputedStats) {
      visitsToday = analytics.visitsToday;
      visitsThisWeek = analytics.visitsThisWeek;
      visitsThisMonth = analytics.visitsThisMonth;
    } else {
      // Estimate recent visits based on active users and their visit patterns
      const recentlyActiveUsers = nonAdminUsers.filter(
        (user) =>
          user.lastVisited && dayjs().diff(dayjs(user.lastVisited), "day") <= 1,
      );
      const weeklyActiveUsers = nonAdminUsers.filter(
        (user) =>
          user.lastVisited && dayjs().diff(dayjs(user.lastVisited), "day") <= 7,
      );
      const monthlyActiveUsers = nonAdminUsers.filter(
        (user) =>
          user.lastVisited &&
          dayjs().diff(dayjs(user.lastVisited), "month") === 0,
      );

      // Estimate visits based on active user patterns
      visitsToday = recentlyActiveUsers.length * 2; // Estimate 2 visits per active user today
      visitsThisWeek = weeklyActiveUsers.length * avgVisitsPerUser * 0.3; // 30% of weekly visits
      visitsThisMonth = monthlyActiveUsers.length * avgVisitsPerUser * 0.8; // 80% of monthly visits
    }

    // Most productive users (excluding admin users)
    // Only available when users are loaded
    const topUsers = hasUsers
      ? [...nonAdminUsers]
          .sort((a, b) => (b.tasksCreated || 0) - (a.tasksCreated || 0))
          .slice(0, 3)
      : [];

    // Activity status - use pre-computed activeUsers when available
    const getActivityStatus = () => {
      if (nonAdminUserCount === 0)
        return { label: "No Data", color: "default" };
      if (activeUsers / nonAdminUserCount > 0.7)
        return { label: "Excellent", color: "success" };
      if (activeUsers / nonAdminUserCount > 0.5)
        return { label: "Good", color: "primary" };
      if (activeUsers / nonAdminUserCount > 0.3)
        return { label: "Fair", color: "warning" };
      return { label: "Low", color: "error" };
    };

    const activityStatus = getActivityStatus();

    // Calculate subscription statistics
    // Use pre-computed stats when available, otherwise calculate from users
    let subscribedUsers, monthlyRevenue;

    if (hasPreComputedStats) {
      subscribedUsers = analytics.subscribedUsers;
      monthlyRevenue = analytics.monthlyRevenue;
    } else {
      // Calculate from users array (ALL users, including admins for revenue tracking)
      subscribedUsers = users.filter((user) => {
        const subscription = user.subscription;
        if (!subscription) return false;

        // Count users with active or cancelling subscriptions (they're still paying until end date)
        const status = subscription.subscriptionStatus;
        const isSubscribed =
          status === "ACTIVE" ||
          status === "CANCELLING" ||
          status === "PAST_DUE";

        return isSubscribed;
      }).length;

      // Calculate monthly revenue (£3 per subscription)
      monthlyRevenue = subscribedUsers * 3;
    }

    return {
      // Raw data
      totalUsers,
      totalVisits,
      tasksCompleted,
      tasksCreated,
      tasksDeleted,
      // Filtered data
      nonAdminUsers,
      nonAdminUserCount,
      nonAdminTasksCreated,
      nonAdminTasksCompleted,
      nonAdminTotalVisits,
      visitsToday,
      visitsThisWeek,
      visitsThisMonth,
      // Calculated metrics
      completionRate,
      deletionRate,
      avgVisitsPerUser,
      avgTasksPerUser,
      activeUsers,
      newUsersThisWeek,
      newUsersToday,
      newUsersThisMonth,
      tasksCreatedThisWeek,
      tasksCreatedToday,
      tasksCreatedThisMonth,
      topUsers,
      activityStatus,
      // Subscription metrics
      subscribedUsers,
      monthlyRevenue,
    };
  }, [analytics, users]);
};

export default useStatsCalculations;
